import { useEffect, useRef, useState } from "react";
import DataGrid, { Column, Scrolling } from "devextreme-react/data-grid";
import { IconButton, Link } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { isEmpty, isNull } from "lodash";

import { dataGridConfig } from "../../../constants";
import { convertUTCTimeToLocal } from "../../../util/Utils";
import { DialogBox } from "../../CreateSites/DialogBox";
import CustomPermissionProcessDetails from "./CustomPermissionProcessDetails";
import { SnackbarMessage, useSnackbar } from "../../../hooks";
import { customPermissionClientApi } from "../../../api-client-nswag/taxportal-client-runtime";
import { getStepMapping } from "../permissionStepMapping";
import { useRecoilValue } from "recoil";
import { getAppName } from "../../../cui/commonComponents/AppNameToggle";

const CustomPermissionLevelReport = (props: any) => {
  const { showSnackbar } = useSnackbar();
  const appName = useRecoilValue<string>(getAppName);
  const previousAppNameRef = useRef(appName);

  const [isDisplayModal, setIsDisplayModal] = useState(false);
  const [customPermissionLevelReportData, setCustomPermissionLevelReportData] = useState<any>(null);
  const [selectedProcessData, setSelectedProcessData] = useState<any>(null);
  const hqSiteId = props.data.data.hqSiteId;
  const mdmClientId = props.data.data.mdmClientId;

  useEffect(() => {
    if (previousAppNameRef.current !== appName) {
      previousAppNameRef.current = appName;
      return;
    }

    const controller = new AbortController();
    fetchCustomPermissionData(controller.signal);
    return () => controller.abort();
  }, [appName]);

  const fetchCustomPermissionData = async (signal?: AbortSignal) => {
    try {
      setCustomPermissionLevelReportData({
        pending: true,
        data: [],
        totalCount: 0,
      });
      const response = await customPermissionClientApi.report_GetSiteReport(hqSiteId, appName, signal);
      if (signal?.aborted) return;
      setCustomPermissionLevelReportData({
        pending: false,
        data: response.steps,
      });
    } catch (error) {
      if (signal?.aborted) return;
      setCustomPermissionLevelReportData((prev: any) => ({ ...prev, pending: false, data: [] }));
      showSnackbar({ message: SnackbarMessage.ERROR, severity: "error" });
    }
  };

  const handleProcessRun = async (data: any) => {
    try {
      const { stepName, iSheetType } = getStepMapping(data?.stepName);
      
      const response = await customPermissionClientApi.customPermission_OnDemand(
        hqSiteId ?? props.data.data.hqSiteId,
        mdmClientId ?? props.data.data.mdmClientId,
        stepName,
        iSheetType
      );

      // typed response: DemandProcessRunResponse
      const success = response?.success;
      const message = response?.message;

      if (success) {
        showSnackbar({
          message: (message || "Run Custom Permission triggered successfully.") as SnackbarMessage,
          severity: "success",
        });
      } else {
        showSnackbar({
          message: (message || "Something went wrong.") as SnackbarMessage,
          severity: "error",
        });
      }
    } catch (error) {
      showSnackbar({
        message: SnackbarMessage.ERROR,
        severity: "error",
      });
    }
  };

  const handleSeeDetailsButton = (data: any) => {
    setSelectedProcessData(data);
    setIsDisplayModal(true);
  };

  const cellRenderStatusDetails = (e: any) => {
    return (
      <IconButton
        className="dx-datagrid dx-link"
        aria-label="See Details"
        title="See Details"
        onClick={() => handleSeeDetailsButton(e.data)}
        sx={{
          padding: "2px",
          marginTop: "-5px",
          marginBottom: "-5px",
          fontSize: "16px",
        }}
      >
        <SearchIcon />
      </IconButton>
    );
  };


  const createdDateTime = (e: any) => {
    if (!isNull(e.data.status) && !isEmpty(e.data.status)) {
      const raw = e?.value ?? e?.data?.lastUpdated ?? e?.data?.created;
      if (!raw) return null;

      return (
        <div style={{ display: "flex" }}>
          <div>
            <div>{convertUTCTimeToLocal(raw)}</div>
          </div>
        </div>
      );
    } else return null;
  };

  const retryButton = (celldata: any) => {
    return (
      <div>
        <Link component="button" variant="body2" onClick={() => handleProcessRun(celldata?.data)}>
          Run
        </Link>
      </div>
    );
  };

  const handleClose = () => {
    setIsDisplayModal(false);
  };

  return (
    <div>
      <DataGrid
        {...dataGridConfig}
        dataSource={customPermissionLevelReportData?.data ?? []}
        showBorders={true}
        allowColumnResizing={true}
        showRowLines={true}
        columnAutoWidth={true}
        height={"100%"}
        noDataText={customPermissionLevelReportData?.pending ? "Loading..." : "No Data"}
      >
        <Scrolling
          mode="virtual"
          scrollByContent={true}
          rowRenderingMode="virtual"
          showScrollbar={true}
        />
        <Column
          dataField="stepName"
          width={400}
          caption="Process"
          allowSorting={true}
        />
        <Column width={80} dataField="status" caption="Status" />
        <Column
          cellRender={cellRenderStatusDetails}
          type="buttons"
          caption="Status Details"
        />
        <Column
          width={100}
          dataField="Run Process"
          caption="Action"
          cellRender={retryButton}
        />
        <Column dataField="lastUpdatedBy" caption="Last Updated By" />
        <Column
          cellRender={createdDateTime}
          dataField="lastModified"
          caption="Last Updated Date"
        />
      </DataGrid>
      <DialogBox
        maxWidth="lg"
        isDisplayModal={isDisplayModal}
        handleClose={handleClose}
        title={"Process Details"}
        btnCloseTop={false}
        btnCloseText={"Close"}
      >
        <CustomPermissionProcessDetails selectedProcessData={selectedProcessData} hqSiteId={hqSiteId}  />
      </DialogBox>
    </div>
  );
};

export default CustomPermissionLevelReport;
